import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useMatch, useNavigate } from 'react-router-dom';

import { forgotPassword } from 'features/auth/api/forgot-password';
import { clearLocalStorageData } from 'shared/lib/authUtils';
import { hasSQLInjection } from 'shared/lib/secure';
import { getErrorMessage } from 'shared/utils/errorMessage';

import {
  RESET_LINK_CONFIG,
  RESET_LINK_MODE,
} from '../constants/resetLink.constants';

const useSendResetLink = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isModeFlow = !!useMatch('/change-password/*');

  const mode = isModeFlow ? RESET_LINK_MODE.CHANGE : RESET_LINK_MODE.FORGOT;
  const config = RESET_LINK_CONFIG[mode];

  const [linkReset, setLinkReset] = useState(false);
  const [formData, setFormData] = useState({ email: '' });
  const [errors, setErrors] = useState({ email: '', error: '' });

  const { mutate: sendEmail, isLoading } = useMutation(
    (payload) => forgotPassword(payload),
    {
      onSuccess: () => {
        if (isModeFlow) clearLocalStorageData();
        setLinkReset(true);
      },
      onError: (error) => {
        setLinkReset(false);
        setErrors((prev) => ({ ...prev, error: getErrorMessage(error) }));
      },
    }
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '', error: '' }));
  };

  const handleSendEmail = () => {
    const { email } = formData;
    const newErrors = {};

    if (email === '') newErrors.email = t('email_required');
    if (hasSQLInjection(email)) newErrors.email = t('email_invalid');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    sendEmail(formData);
  };

  const redirectLogin = () => {
    navigate('/login');
  };

  return {
    formData,
    errors,
    loading: isLoading,
    linkReset,
    handleChange,
    handleSendEmail,
    redirectLogin,
    config,
  };
};

export default useSendResetLink;
